import _ from 'lodash';
import type { Enable, ResizeCallback } from 're-resizable';
import { Resizable } from 're-resizable';
import React, { useMemo } from 'react';

import type { PluginContainerData } from './../../ricos-schema/next-schema';
import type { PluginProps } from './../../ricos-types';

import styles from '../statics/styles-scss';

// Resizable applies inline handle styles that override our stylesheet styles. We need to kindly
// undo some of them.
const handleStyleReset: React.CSSProperties = {
  width: undefined,
  height: undefined,
  top: undefined,
  bottom: undefined,
  left: undefined,
  right: undefined,
};

const handleStyles = {
  left: handleStyleReset,
  right: handleStyleReset,
};

const handleClasses = {
  right: [styles.handlerRight, 'ricos-resize-handler-right'].join(' '),
  left: styles.handlerLeft,
};

const getEnableMap = (disabled: boolean, containerData?: PluginContainerData | null): Enable => {
  if (disabled) {
    return { left: false, right: false };
  }

  switch (containerData?.alignment) {
    case 'LEFT':
      return { left: false, right: true };
    case 'RIGHT':
      return { left: true, right: false };
    default:
      return { left: true, right: true };
  }
};

const useMaxContentWidth = (props: PluginProps) => {
  const { editor, decorations } = props;

  return useMemo(() => {
    const isBreakoutFullWidth = decorations.some(
      (decoration: $TSFixMe) => decoration.type?.attrs['data-breakout'] === 'fullWidth'
    );

    if (isBreakoutFullWidth) {
      return editor.view.dom.clientWidth;
    }

    const editorContent = window.getComputedStyle(editor.view.dom.firstChild as HTMLElement);
    const paddingStart = parseInt(editorContent.paddingInlineStart) || 0;
    const paddingEnd = parseInt(editorContent.paddingInlineEnd) || 0;

    return editor.view.dom.clientWidth - paddingStart - paddingEnd;
  }, [editor.view.dom.clientWidth, decorations]);
};

const ResizerHOC = (Component: React.FC<PluginProps>) => {
  return (props: PluginProps) => {
    const { updateAttributes, componentData, selected } = props;
    const minWidth = 15;
    const maxWidth = Math.max(minWidth, useMaxContentWidth(props));
    const enableMap = getEnableMap(!selected, componentData.containerData);

    const onResize: ResizeCallback = (_event, _direction, element) => {
      // width is reset to "auto" if user clicks handle without dragging it, ignore such cases
      const width = parseInt(element.style.width);

      if (Number.isInteger(width)) {
        updateAttributes({
          containerData: {
            ...props.componentData.containerData,
            width: {
              custom: width,
            },
          },
        });
      }
    };

    // FIXME: throttling is needed to prevent the editor from freezing up while some element is
    // being resized. However, the throttled size is applied to the Component and its container
    // while the Resizer itself is "sandwiched" between the two and will always be the immediate
    // (not throttled) size as it handles its own size changes internally. This results in a
    // visual glitch as the Resizer's size is not in sync with the debounced Component's size
    // while resizing.
    const onResizeThrottle = _.throttle(onResize, 30);

    return (
      <Resizable
        data-hook="ricos-resizer"
        minWidth={minWidth}
        maxWidth={maxWidth}
        size={{ width: 'auto', height: 'auto' }}
        enable={enableMap}
        className={styles.resizer}
        onResize={onResizeThrottle}
        onResizeStop={onResize}
        handleClasses={handleClasses}
        handleStyles={handleStyles}
      >
        <Component {...props} />
      </Resizable>
    );
  };
};

export default ResizerHOC;
